import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  PARTICIPANT_STATUSES,
  type PlayerCategory,
  type TournamentParticipantDetail,
} from "../lib/api";
import RankMovementBadge from "../components/RankMovementBadge";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { formatDateTime } from "../lib/dateFormat";
import { useIsDraftTournament } from "../lib/draftTournamentContext";
import { impersonatePlayer } from "../lib/playerImpersonation";
import { accountNotOwnedInfo, accountNotOwnedNote, canManagePlayerAccount } from "../lib/playerAccountOwnership";
import TournamentBottomNav from "../components/TournamentBottomNav";
import GuideHelpButton from "../components/GuideHelpButton";

const STATUS_LABELS: Record<string, string> = {
  pre_registered: "Pre-registrado",
  active: "Activo",
  confirmed: "Confirmado",
  dropped: "Dado de baja",
};

function formatStatus(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/** Revisión 2026-10-05: en eliminatoria o playoffs cancelar no hace avanzar a
 * nadie; ahí el servidor da W.O. a su rival. */
const DROP_BRACKET_WALKOVER_TEXT =
  "Los de eliminatoria o playoffs quedan como W.O. para su rival, para que avance en el cuadro.";

export default function AdminTournamentPlayerDetailPage() {
  const { id, participantId } = useParams<{
    id: string;
    participantId: string;
  }>();
  const tournamentId = id ? Number(id) : NaN;
  const participantIdNum = participantId ? Number(participantId) : NaN;
  const { askConfirm } = useAbortableConfirm([tournamentId, participantIdNum]);
  const { token, impersonateWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Borrador (revisión 2026-10-02): se llega aquí desde «Ver / editar» del
  // paso Jugadores del asistente; «Volver» regresa a ese paso, no a la
  // sección de operación (que rebota a Publicar).
  const draft = useIsDraftTournament();
  const backDivision = new URLSearchParams(location.search).get("division");
  const backTo = `/admin/tournaments/${tournamentId}${draft ? "/setup" : ""}/players${
    backDivision ? `?division=${encodeURIComponent(backDivision)}` : ""
  }`;

  const [participant, setParticipant] =
    useState<TournamentParticipantDetail | null>(null);
  const [categories, setCategories] = useState<PlayerCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [notes, setNotes] = useState("");
  const [statusValue, setStatusValue] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [reason, setReason] = useState("");
  // Política de partidos pendientes al dar de baja (paquete de baja
  // 2026-08-28): W/O al rival es el default por regla de negocio
  // (walkover = victoria); "none" deja los partidos como están.
  const [pendingMatchesAction, setPendingMatchesAction] = useState<
    "walkover" | "cancel" | "none"
  >("walkover");
  const [initialRank, setInitialRank] = useState<string>("");
  const [currentRank, setCurrentRank] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [actingDrop, setActingDrop] = useState(false);
  const [actingReactivate, setActingReactivate] = useState(false);
  const [cancellingPending, setCancellingPending] = useState(false);
  const [impersonatingBusy, setImpersonatingBusy] = useState(false);

  const loadParticipant = useCallback(async () => {
    if (!token || Number.isNaN(tournamentId) || Number.isNaN(participantIdNum))
      return;
    setLoading(true);
    setError(null);
    try {
      const response = await api.participants.get(
        token,
        tournamentId,
        participantIdNum,
      );
      const p = response.data.participant;
      setParticipant(p);
      setNotes(p.notes ?? "");
      setStatusValue(p.current_status);
      setCategoryId(
        p.current_category ? String(p.current_category.id) : "",
      );
      setInitialRank(p.initial_rank != null ? String(p.initial_rank) : "");
      setCurrentRank(p.current_rank != null ? String(p.current_rank) : "");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No pudimos cargar la inscripción.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setLoading(false);
    }
  }, [token, tournamentId, participantIdNum]);

  const loadCategories = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.common.playerCategories(token);
      setCategories(response.data.categories);
    } catch {
      // non-critical
    }
  }, [token]);

  useEffect(() => {
    void loadParticipant();
    void loadCategories();
  }, [loadParticipant, loadCategories]);

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !participant) return;
    setError(null);
    setMessage(null);
    const initialRaw = initialRank.trim();
    const currentRaw = currentRank.trim();
    const initialValue =
      initialRaw === ""
        ? null
        : Number.isNaN(Number(initialRaw)) || Number(initialRaw) < 1
          ? "invalid"
          : Math.trunc(Number(initialRaw));
    const currentValue =
      currentRaw === ""
        ? null
        : Number.isNaN(Number(currentRaw)) || Number(currentRaw) < 1
          ? "invalid"
          : Math.trunc(Number(currentRaw));
    if (initialValue === "invalid" || currentValue === "invalid") {
      setError("Los rangos deben ser números mayores a 0.");
      return;
    }
    setSaving(true);
    try {
      const response = await api.participants.update(
        token,
        tournamentId,
        participant.id,
        {
          status: statusValue || null,
          notes: notes,
          category_id: categoryId ? Number(categoryId) : null,
          initial_rank: initialValue,
          current_rank: currentValue,
        },
      );
      // Refresh detail to get latest history.
      const refreshed = await api.participants.get(
        token,
        tournamentId,
        response.data.participant.id,
      );
      setParticipant(refreshed.data.participant);
      setMessage("Cambios guardados.");
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudieron guardar los cambios.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDrop() {
    if (!token || !participant) return;
    setError(null);
    setMessage(null);
    setActingDrop(true);
    try {
      const response = await api.participants.drop(token, tournamentId, participant.id, {
        reason: reason.trim() || null,
        pending_matches_action:
          pendingMatchesAction === "none" ? null : pendingMatchesAction,
      });
      setReason("");
      const summary = response.data.drop_summary;
      const details: string[] = [];
      if (summary.walkovers > 0) {
        details.push(
          summary.walkovers === 1
            ? "1 partido pendiente pasó a W/O para el rival"
            : `${summary.walkovers} partidos pendientes pasaron a W/O para el rival`,
        );
      }
      if (summary.cancelled > 0) {
        details.push(
          summary.cancelled === 1
            ? "1 partido pendiente se canceló"
            : `${summary.cancelled} partidos pendientes se cancelaron`,
        );
      }
      const kept = summary.kept_in_bracket ?? 0;
      if (kept > 0) {
        details.push(
          kept === 1
            ? "1 partido del cuadro sigue vivo: su rival gana por W.O. al llegar"
            : `${kept} partidos del cuadro siguen vivos: su rival gana por W.O. al llegar`,
        );
      }
      const leftOpen = summary.left_in_progress ?? 0;
      if (leftOpen > 0) {
        details.push(
          leftOpen === 1
            ? "1 partido en juego sigue abierto: ciérralo con W.O. en Resultados"
            : `${leftOpen} partidos en juego siguen abiertos: ciérralos con W.O. en Resultados`,
        );
      }
      if (summary.group_removed > 0) {
        details.push("se liberó su lugar en el grupo");
      }
      setMessage(
        details.length > 0
          ? `Jugador dado de baja: ${details.join(", ")}.`
          : "Jugador dado de baja.",
      );
      await loadParticipant();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo dar de baja al jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setActingDrop(false);
    }
  }

  async function handleReactivate() {
    if (!token || !participant) return;
    setError(null);
    setMessage(null);
    setActingReactivate(true);
    try {
      await api.participants.reactivate(token, tournamentId, participant.id, {
        reason: reason.trim() || null,
      });
      setReason("");
      setMessage("Jugador reactivado.");
      await loadParticipant();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo reactivar al jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setActingReactivate(false);
    }
  }

  /** Eduardo 2026-10-05 (torneo 69): si al dar de baja se eligió «Dejarlos
   * como están», sus partidos seguían como por jugar. Los cancela con la
   * nota «Baja de <nombre>»; esa jornada su rival descansa. */
  async function handleCancelPending(count: number, walkoverCount: number) {
    if (!token || !participant) return;
    const name = participant.player?.display_name ?? "este jugador";
    const confirmed = await askConfirm(
      `¿Cancelar ${count === 1 ? "el partido pendiente" : `los ${count} partidos pendientes`} de ${name}?\n` +
        "Quedan como «Cancelado por baja» (no cuentan como jugados) y, en esa jornada, su rival descansa." +
        (walkoverCount > 0 ? `\n${DROP_BRACKET_WALKOVER_TEXT}` : ""),
      { acceptLabel: count === 1 ? "Sí, cancelar el partido" : "Sí, cancelar los partidos", tone: "danger" },
    );
    if (!confirmed) return;
    setError(null);
    setMessage(null);
    setCancellingPending(true);
    try {
      const response = await api.participants.cancelPendingMatches(token, tournamentId, participant.id);
      const cancelled = response.data.cancelled;
      const walkovers = response.data.walkovers ?? 0;
      const kept = response.data.kept_in_bracket ?? 0;
      const parts = [
        cancelled === 1 ? "Se canceló 1 partido pendiente por la baja." : cancelled > 1 ? `Se cancelaron ${cancelled} partidos pendientes por la baja.` : "",
        walkovers === 1 ? "1 de eliminatoria quedó como W.O. para su rival." : walkovers > 1 ? `${walkovers} de eliminatoria quedaron como W.O. para sus rivales.` : "",
        kept === 1 ? "1 del cuadro sigue vivo: su rival gana por W.O. al llegar." : kept > 1 ? `${kept} del cuadro siguen vivos: su rival gana por W.O. al llegar.` : "",
      ].filter(Boolean);
      setMessage(parts.length > 0 ? parts.join(" ") : "No le quedaban partidos pendientes.");
      await loadParticipant();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudieron cancelar sus partidos pendientes.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setCancellingPending(false);
    }
  }

  async function handleImpersonatePlayer() {
    if (!token || !participant?.player) return;
    setError(null);
    setMessage(null);
    setImpersonatingBusy(true);
    try {
      await impersonatePlayer(token, participant.player.id, impersonateWithToken);
      navigate("/player", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || "No se pudo entrar como jugador.");
      } else {
        setError("Error de red. Intenta otra vez.");
      }
    } finally {
      setImpersonatingBusy(false);
    }
  }

  if (Number.isNaN(tournamentId) || Number.isNaN(participantIdNum)) {
    return (
      <section className="card">
        <p className="form-error">Ruta inválida.</p>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="card">
        <p className="muted">Cargando…</p>
      </section>
    );
  }

  if (!participant) {
    return (
      <section className="card">
        <p className="form-error">{error ?? "Inscripción no encontrada."}</p>
        <Link
          className="btn btn--ghost"
          to={backTo}
        >
          Volver
        </Link>
      </section>
    );
  }

  const player = participant.player;
  const isDropped = participant.current_status === "dropped";
  const pendingMatchesCount = participant.pending_matches_count ?? 0;
  const pendingWalkoverCount = participant.pending_matches_walkover_count ?? 0;

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <h1 className="card__title">
              <span className="hint-label">
                {player?.display_name ?? `Jugador #${participant.player_profile_id}`}
                <GuideHelpButton />
              </span>
            </h1>
            <p className="card__subtitle">
              <span className="kbd">{formatStatus(participant.current_status)}</span>
              {participant.current_category && (
                <>
                  {" "}
                  <span className="kbd">{participant.current_category.name}</span>
                </>
              )}
            </p>
          </div>
          <div className="form__actions">
            {player && canManagePlayerAccount(player) && (
              <button
                type="button"
                className="btn"
                onClick={handleImpersonatePlayer}
                disabled={impersonatingBusy || player.active_status !== "active"}
              >
                {impersonatingBusy ? "Entrando…" : "Entrar como jugador"}
              </button>
            )}
            {player && (
              <Link
                className="btn btn--ghost"
                to={`/admin/players/${player.id}`}
              >
                Perfil global
              </Link>
            )}
            <Link
              className="btn btn--ghost"
              to={backTo}
            >
              Volver
            </Link>
          </div>
        </div>
        {error && (
          <p role="alert" className="form-error">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
        {player && !canManagePlayerAccount(player) ? (
          // Lo registró otra organización (Opción A, Eduardo 2026-09-30): sin
          // «Entrar como jugador». Aviso fijo, no un error al pulsar.
          <p className="muted account-not-owned-note" role="note" data-no-toast>
            {accountNotOwnedNote(accountNotOwnedInfo(player))}
          </p>
        ) : null}
      </section>

      {player && (
        <section className="card">
          <h2 className="card__title">Datos del jugador</h2>
          <ul className="list">
            <li>
              <strong>Nombre:</strong> {player.display_name}
            </li>
            {player.email && (
              <li>
                <strong>Email:</strong> {player.email}
              </li>
            )}
            {player.phone && (
              <li>
                <strong>Teléfono:</strong> {player.phone}
              </li>
            )}
            {player.city && (
              <li>
                <strong>Ciudad:</strong> {player.city}
              </li>
            )}
            {player.primary_category && (
              <li>
                <strong>Categoría global:</strong>{" "}
                {player.primary_category.name}
              </li>
            )}
            <li>
              <strong>Inscrito:</strong> {formatDateTime(participant.joined_at)}
            </li>
            {participant.dropped_at && (
              <li>
                <strong>Baja:</strong> {formatDateTime(participant.dropped_at)}
              </li>
            )}
          </ul>
        </section>
      )}

      <section className="card">
        <h2 className="card__title">Actualizar inscripción</h2>
        <form className="form" onSubmit={handleSave}>
          <label>
            Estatus
            <select
              value={statusValue}
              onChange={(e) => setStatusValue(e.target.value)}
              disabled={saving}
            >
              {PARTICIPANT_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {formatStatus(s)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Categoría
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={saving || categories.length === 0}
            >
              <option value="">Sin categoría</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Rank inicial
            <input
              type="number"
              min={1}
              value={initialRank}
              onChange={(e) => setInitialRank(e.target.value)}
              disabled={saving}
              placeholder="Sin asignar"
            />
          </label>
          <label>
            Rank actual
            <input
              type="number"
              min={1}
              value={currentRank}
              onChange={(e) => setCurrentRank(e.target.value)}
              disabled={saving}
              placeholder="Sin asignar"
            />
          </label>
          <p className="muted" style={{ margin: 0 }}>
            Cambio actual:{" "}
            <RankMovementBadge
              movement={participant.rank_movement}
              delta={participant.rank_delta}
            />
          </p>
          <label>
            Notas
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={saving}
            />
          </label>
          <div className="form__actions">
            <button className="btn" type="submit" disabled={saving}>
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </section>

      <section className="card">
        <h2 className="card__title">Acciones</h2>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (isDropped) {
              void handleReactivate();
            } else {
              void handleDrop();
            }
          }}
        >
          <label>
            Motivo (opcional)
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={actingDrop || actingReactivate}
              placeholder={
                isDropped ? "Motivo de reactivación" : "Motivo de baja"
              }
            />
          </label>
          {!isDropped ? (
            <label>
              ¿Qué hacer con sus partidos pendientes?
              <select
                value={pendingMatchesAction}
                onChange={(e) =>
                  setPendingMatchesAction(
                    e.target.value as "walkover" | "cancel" | "none",
                  )
                }
                disabled={actingDrop}
              >
                <option value="walkover">
                  Dar W/O al rival (cuentan como victoria del rival)
                </option>
                <option value="cancel">Cancelar los partidos pendientes</option>
                <option value="none">Dejarlos como están</option>
              </select>
            </label>
          ) : null}
          <div className="form__actions">
            {isDropped ? (
              <button
                className="btn"
                type="submit"
                disabled={actingReactivate}
              >
                {actingReactivate ? "Reactivando…" : "Reactivar"}
              </button>
            ) : (
              <button
                className="btn btn--ghost"
                type="submit"
                disabled={actingDrop}
              >
                {actingDrop ? "Dando de baja…" : "Dar de baja"}
              </button>
            )}
          </div>
        </form>
        {isDropped && pendingMatchesCount > 0 ? (
          <div className="drop-pending-matches" data-no-toast data-drop-pending-matches>
            <p className="muted">
              {pendingMatchesCount === 1
                ? "Le queda 1 partido pendiente: sigue apareciendo como por jugar en Calendario y Resultados."
                : `Le quedan ${pendingMatchesCount} partidos pendientes: siguen apareciendo como por jugar en Calendario y Resultados.`}{" "}
              Al cancelarlos quedan como «Cancelado por baja» y su rival descansa esa jornada.
              {pendingWalkoverCount > 0 ? ` ${DROP_BRACKET_WALKOVER_TEXT}` : ""}
            </p>
            <div className="form__actions">
              <button
                type="button"
                className="btn btn--danger"
                onClick={() => void handleCancelPending(pendingMatchesCount, pendingWalkoverCount)}
                disabled={cancellingPending || actingReactivate}
              >
                {cancellingPending
                  ? "Cancelando…"
                  : `Cancelar sus partidos pendientes (${pendingMatchesCount})`}
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="card">
        <h2 className="card__title">Historial de estatus</h2>
        {participant.status_history.length === 0 ? (
          <p className="muted">Sin historial.</p>
        ) : (
          <ul className="list">
            {participant.status_history.map((h) => (
              <li key={h.id}>
                <strong>{formatStatus(h.new_status)}</strong>
                {h.old_status && (
                  <span className="muted">
                    {" "}
                    · desde {formatStatus(h.old_status)}
                  </span>
                )}
                <span className="muted"> · {formatDateTime(h.changed_at)}</span>
                {h.reason && <span className="muted"> · {h.reason}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {participant.category_history.length > 0 && (
        <section className="card">
          <h2 className="card__title">Historial de categoría</h2>
          <ul className="list">
            {participant.category_history.map((a) => (
              <li key={a.id}>
                <strong>
                  {a.category ? a.category.name : `#${a.player_category_id}`}
                </strong>
                <span className="muted"> · {formatDateTime(a.assigned_at)}</span>
                {a.is_current && <span className="muted"> · vigente</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
      <TournamentBottomNav tournamentId={tournamentId} />
    </>
  );
}
